import { Link } from 'react-router'
import { FileText } from 'lucide-react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { PageHeader } from '@/components/data/page-header'
import { InvoiceStatus } from '@/components/data/badges'
import { FilterSelect, PAGE_SIZE, Pagination, SearchBox } from '@/components/data/list-controls'
import { MonoId } from '@/components/data/mono-id'
import { EmptyState, ErrorState, SkeletonRows } from '@/components/data/states'
import { useListParams } from '@/hooks/use-list-params'
import { formatDate, formatMoney } from '@/lib/format'
import { useInvoices } from '@/lib/queries'

export default function InvoicesPage() {
  const { params, set } = useListParams()
  const { data, error, isPending, isFetching, refetch } = useInvoices(params)
  const offset = Number(params.offset ?? 0)
  const filtered = !!(params.status || params.search)

  return (
    <>
      <PageHeader title="Invoices">
        {data ? <>{data.total.toLocaleString('en-US')} {filtered ? 'matching' : 'stored'}</> : 'Every invoice Stripe has sent, kept as history.'}
      </PageHeader>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <SearchBox value={params.search ?? ''} onChange={(search) => set({ search })} placeholder="Invoice number, team, Stripe id" />
        <FilterSelect
          label="Status"
          value={params.status ?? ''}
          onChange={(status) => set({ status })}
          options={[
            { value: 'paid', label: 'Paid' },
            { value: 'open', label: 'Open' },
            { value: 'uncollectible', label: 'Written off' },
            { value: 'void', label: 'Void' },
            { value: 'draft', label: 'Draft' },
          ]}
        />
      </div>

      <div className="panel overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Invoice</TableHead>
                <TableHead>Team</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Period</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="w-0" />
              </TableRow>
            </TableHeader>
            <TableBody className={isFetching && !isPending ? 'opacity-70' : undefined}>
              {isPending ? (
                <SkeletonRows columns={7} />
              ) : (
                data?.items.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-medium">{inv.number || <MonoId value={inv.stripe_invoice_id} />}</TableCell>
                    <TableCell>
                      {inv.team_id ? (
                        <Link to={`/teams/${inv.team_id}?tab=billing`} className="hover:text-primary hover:underline">
                          {inv.team_name || `Team ${inv.team_id}`}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">Not matched to a team</span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">{formatDate(inv.stripe_created_at)}</TableCell>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {inv.period_start ? `${formatDate(inv.period_start)} – ${formatDate(inv.period_end)}` : '—'}
                    </TableCell>
                    <TableCell>
                      <InvoiceStatus status={inv.status} attempts={inv.attempt_count} />
                    </TableCell>
                    <TableCell className="figures text-right">{formatMoney(inv.status === 'paid' ? inv.amount_paid : inv.amount_due, inv.currency)}</TableCell>
                    <TableCell>
                      {inv.hosted_invoice_url && (
                        <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                          <FileText className="size-3.5" /> View
                        </a>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        {error && <ErrorState error={error} onRetry={() => refetch()} />}
        {!isPending && !error && data?.items.length === 0 && (
          <EmptyState title={filtered ? 'No invoice matches' : 'No invoices stored yet'}>
            {filtered ? 'Remove a filter to see more.' : 'Invoices are saved as Stripe sends invoice events. In Stripe, subscribe the webhook endpoint to the invoice events, and run the backfill command to import earlier ones.'}
          </EmptyState>
        )}
        <Pagination total={data?.total ?? 0} offset={offset} pageSize={PAGE_SIZE} onChange={(o) => set({ offset: String(o) })} />
      </div>
    </>
  )
}
